import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useJob } from "../api/jobs";
import { useCoursePage, useSyncAll } from "../api/queries";
import type { MoodleCourse } from "../api/types";
import { JobNotice } from "../components/JobNotice";
import { SyncDialog } from "../components/SyncDialog";
import { ErrorBanner, Loading, RelativeTime } from "../components/ui";
import {
  BrandButton,
  CoursePageHeader,
  DataTable,
  EmptyState,
  HeadCell,
  NumberPager,
  RowCell,
  SearchInput,
  SelectInput,
} from "../components/courses/ui";
import { pad, rowClass } from "../components/courses/styles";

const PAGE_SIZE = 50;

type Visibility = "all" | "visible" | "hidden";

export function CoursesPage() {
  const [offset, setOffset] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("");
  const [format, setFormat] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const courses = useCoursePage(offset, PAGE_SIZE, includeDeleted);
  const syncAll = useSyncAll();
  const syncJob = useJob("sync");

  const rows = useMemo(() => courses.data?.courses ?? [], [courses.data]);
  // Category and type options come from the courses on this page: the API
  // has no category names or a server-side filter for them.
  const categories = useMemo(() => uniq(rows.map((c) => c.category_id)), [rows]);
  const formats = useMemo(() => uniq(rows.map((c) => c.course_format)), [rows]);

  const needle = filter.trim().toLowerCase();
  const visible = rows.filter(
    (c) =>
      (!needle || c.fullname.toLowerCase().includes(needle) || c.shortname.toLowerCase().includes(needle)) &&
      (!category || String(c.category_id) === category) &&
      (!format || c.course_format === format) &&
      (visibility === "all" || (visibility === "visible") === c.visible)
  );
  const filtered = !!(needle || category || format || visibility !== "all");
  const total = courses.data?.total;

  return (
    <div className="px-8 py-10">
      <CoursePageHeader
        back="/"
        title="Courses"
        subtitle="Courses mirrored from Moodle, with how much of each is extracted, reviewed and indexed for the tutor."
        actions={
          <BrandButton
            variant="primary"
            className="min-h-[62px] px-5"
            loading={syncAll.isPending}
            disabled={!!syncJob}
            onClick={() => setSyncOpen(true)}
          >
            <SyncIcon spinning={!!syncJob} />
            {syncJob ? "Sync running…" : "Sync From Moodle"}
          </BrandButton>
        }
      />
      {syncOpen && (
        <SyncDialog
          onClose={() => setSyncOpen(false)}
          onSync={(ids) => {
            syncAll.mutate(ids);
            setSyncOpen(false);
          }}
        />
      )}

      <div className="mt-6 space-y-5 lg:pl-10">
        <ErrorBanner error={syncAll.error} />
        <JobNotice kind="sync" />

        <div className="flex flex-wrap items-center gap-5">
          <SearchInput value={filter} onChange={setFilter} placeholder="Search ..." className="min-w-[240px] flex-1 lg:max-w-[790px]" />
          <div className="flex flex-wrap items-center gap-5 lg:ml-auto">
            <SelectInput aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-40">
              <option value="">Category</option>
              {categories.map((id) => <option key={id} value={String(id)}>Category {id}</option>)}
            </SelectInput>
            <SelectInput aria-label="Type" value={format} onChange={(e) => setFormat(e.target.value)} className="w-40">
              <option value="">Type</option>
              {formats.map((f) => <option key={f} value={f}>{f}</option>)}
            </SelectInput>
            <div className="relative">
              <button
                aria-label="More filters"
                aria-expanded={showFilters}
                onClick={() => setShowFilters((v) => !v)}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-md bg-[#0B5785] text-white shadow-sm transition-colors hover:bg-[#0a4a71]"
              >
                <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path d="M3 6h18M6 12h12M10 18h4" strokeLinecap="round" />
                </svg>
              </button>
              {showFilters && (
                <div className="absolute right-0 z-20 mt-2 w-60 space-y-3 rounded-lg border border-gray-100 bg-white p-4 text-sm shadow-lg">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-500">Visibility</span>
                    <SelectInput value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
                      <option value="all">All</option>
                      <option value="visible">Visible</option>
                      <option value="hidden">Hidden</option>
                    </SelectInput>
                  </label>
                  <label className="flex items-center gap-2 text-gray-700">
                    <input
                      type="checkbox"
                      className="accent-[#0B5785]"
                      checked={includeDeleted}
                      onChange={(e) => {
                        setIncludeDeleted(e.target.checked);
                        setOffset(0);
                      }}
                    />
                    Include removed courses
                  </label>
                  {filtered && (
                    <button
                      className="text-xs font-medium text-[#0B5785] hover:underline"
                      onClick={() => {
                        setFilter("");
                        setCategory("");
                        setFormat("");
                        setVisibility("all");
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <ErrorBanner error={courses.error} />
        {courses.isLoading ? <Loading /> : !visible.length ? (
          <EmptyState>{rows.length ? "No courses on this page match." : "No courses mirrored yet. Run a sync."}</EmptyState>
        ) : (
          <DataTable
            minWidth={1100}
            head={
              <>
                <HeadCell className="w-16">No.</HeadCell>
                <HeadCell>Course</HeadCell>
                <HeadCell>Modules</HeadCell>
                <HeadCell>Failed</HeadCell>
                <HeadCell>Pending</HeadCell>
                <HeadCell>Approved</HeadCell>
                <HeadCell title="Approved and embedded at its current version: what the tutor can retrieve">Indexed</HeadCell>
                <HeadCell>Visibility</HeadCell>
                <HeadCell>Last Synced</HeadCell>
                <HeadCell>Action</HeadCell>
              </>
            }
          >
            {visible.map((c) => <CourseRow key={c.moodle_id} course={c} index={offset + rows.indexOf(c) + 1} />)}
          </DataTable>
        )}
        <NumberPager
          page={Math.floor(offset / PAGE_SIZE)}
          pages={total !== undefined ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : undefined}
          hasNext={total !== undefined ? offset + rows.length < total : rows.length === PAGE_SIZE}
          onChange={(p) => setOffset(p * PAGE_SIZE)}
        />
      </div>
    </div>
  );
}

function CourseRow({ course: c, index }: { course: MoodleCourse; index: number }) {
  const s = c.stats;
  return (
    <tr className={`${rowClass} ${c.deleted_at ? "opacity-60" : ""}`}>
      <RowCell className="tabular-nums">{pad(index)}</RowCell>
      <RowCell>
        <Link to={`/courses/${c.moodle_id}`} className="font-medium text-gray-900 hover:text-[#0B5785] hover:underline">{c.fullname}</Link>
        <div className="mt-0.5 text-xs text-gray-500">{c.shortname} · id {c.moodle_id}</div>
      </RowCell>
      <RowCell className="tabular-nums">{pad(s?.modules)}</RowCell>
      <RowCell className="tabular-nums">
        {s?.extraction_failed ? (
          <Link to={`/failed-extractions?course=${c.moodle_id}`} className="text-red-600 hover:underline">{pad(s.extraction_failed)}</Link>
        ) : pad(s?.extraction_failed)}
      </RowCell>
      <RowCell className="tabular-nums">
        {s?.pending ? (
          <Link to={`/approvals?course=${c.moodle_id}`} className="text-amber-700 hover:underline">{pad(s.pending)}</Link>
        ) : pad(s?.pending)}
      </RowCell>
      <RowCell className="tabular-nums">{pad(s?.approved)}</RowCell>
      <RowCell className="tabular-nums">
        {s && s.approved > s.indexed ? (
          <span className="text-amber-700" title={`${s.approved - s.indexed} approved but not yet indexed`}>{pad(s.indexed)}</span>
        ) : pad(s?.indexed)}
      </RowCell>
      <RowCell>
        {c.deleted_at ? <span className="text-red-600">removed</span> : c.visible ? "visible" : "hidden"}
      </RowCell>
      <RowCell className="whitespace-nowrap"><RelativeTime iso={c.synced_at} /></RowCell>
      <RowCell>
        <Link to={`/courses/${c.moodle_id}`} className="font-medium text-[#0B5785] underline-offset-4 hover:underline">Open</Link>
      </RowCell>
    </tr>
  );
}

function SyncIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-5 w-5 ${spinning ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path d="M20 11a8 8 0 0 0-14.3-4.9L4 8M4 4v4h4M4 13a8 8 0 0 0 14.3 4.9L20 16m0 4v-4h-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function uniq<T>(values: (T | null)[]): T[] {
  return [...new Set(values.filter((v): v is T => v !== null))].sort();
}
